<script setup lang="ts">
import { computed } from "vue";

/**
 * 인라인 SVG 아이콘(의존성 없음). 선 1.75 · `currentColor` 라 글자색을 따른다.
 * - 글리프 아이콘(⌕ ✓ ⚠ × ✕ ✔)을 대체한다. 크기는 14 · 16 · 20 세 가지.
 * - 사이드바 그룹 아이콘 7종(80차 UXS80-01 · C1): `flag`(시작) · `bolt`(새 채용시작) · `blueprint`(맞춤 채용설계) · `building`(조직적합성) ·
 *   `puzzle`(직무적합성) · `briefcase`(채용전형 운영) · `settings`(관리). 이모지(OS 마다 모양이 다르다)를 대신한다.
 * - `label` 이 없으면 장식(`aria-hidden`)이다. 뜻을 아이콘만으로 전할 때(글자 없는 버튼 안 등)만 `label` 을 준다.
 */
export type UiIconName =
  | "alert"
  | "archive"
  | "arrow-down"
  | "arrow-left"
  | "arrow-right"
  | "arrow-up"
  | "blueprint"
  | "bolt"
  | "briefcase"
  | "building"
  | "check"
  | "chevron-down"
  | "chevron-left"
  | "chevron-right"
  | "chevron-up"
  | "close"
  | "copy"
  | "download"
  | "edit"
  | "external"
  | "eye"
  | "eye-off"
  | "filter"
  | "flag"
  | "history"
  | "info"
  | "lock"
  | "menu"
  | "more"
  | "plus"
  | "puzzle"
  | "refresh"
  | "search"
  | "settings"
  | "sparkle"
  | "trash"
  | "upload";

const props = withDefaults(
  defineProps<{
    /** 아이콘 이름 */
    name: UiIconName;
    /** 한 변 크기(px) */
    size?: 14 | 16 | 20;
    /** 화면 낭독 이름. 없으면 장식으로 숨긴다 */
    label?: string;
  }>(),
  { size: 16, label: undefined },
);

/** 24 × 24 격자 위 선 경로 */
const PATHS: Record<UiIconName, readonly string[]> = {
  alert: [
    "M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z",
    "M12 9v4",
    "M12 17h.01",
  ],
  /** 보관함 — 뚜껑 달린 상자(구획 이동 줄 마지막 「보관함」 칸) */
  archive: [
    "M3 3h18a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z",
    "M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8",
    "M10 12h4",
  ],
  "arrow-down": ["M12 5v14", "m19 12-7 7-7-7"],
  "arrow-left": ["M19 12H5", "m12 19-7-7 7-7"],
  "arrow-right": ["M5 12h14", "m12 5 7 7-7 7"],
  "arrow-up": ["M12 19V5", "m5 12 7-7 7 7"],
  /** 설계도 — 판 위에 칸을 나눈 도면(맞춤 채용설계) */
  blueprint: [
    "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z",
    "M3 9h18",
    "M9 21V9",
  ],
  /** 번개(새 채용시작 · 빠르게) */
  bolt: ["M13 2 3 14h9l-1 8 10-12h-9z"],
  briefcase: [
    "M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16",
    "M4 7h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z",
  ],
  building: [
    "M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18z",
    "M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2",
    "M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2",
    "M10 6h4",
    "M10 10h4",
    "M10 14h4",
    "M10 18h4",
  ],
  check: ["M20 6 9 17l-5-5"],
  "chevron-down": ["m6 9 6 6 6-6"],
  "chevron-left": ["m15 18-6-6 6-6"],
  "chevron-right": ["m9 18 6-6-6-6"],
  "chevron-up": ["m18 15-6-6-6 6"],
  close: ["M18 6 6 18", "m6 6 12 12"],
  copy: [
    "M10 8h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2z",
    "M4 16a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2",
  ],
  download: ["M12 3v12", "m7 10 5 5 5-5", "M5 21h14"],
  edit: ["M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z", "m15 5 4 4"],
  external: [
    "M15 3h6v6",
    "M10 14 21 3",
    "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
  ],
  /** 비밀번호 보기 · 숨기기 토글(지금 상태가 아니라 누르면 될 상태를 그린다 — 호출부 몫) */
  eye: [
    "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z",
    "M12 9a3 3 0 1 0 0 6 3 3 0 1 0 0-6z",
  ],
  "eye-off": [
    "M10.6 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-2.2 3.2",
    "M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6",
    "M14.1 14.1a3 3 0 0 1-4.2-4.2",
    "m2 2 20 20",
  ],
  filter: ["M22 3H2l8 9.46V19l4 2v-8.54z"],
  /** 깃발(시작) */
  flag: [
    "M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z",
    "M4 22v-7",
  ],
  /** 이전 · 기록(「이전 후보 n개」) — 시계 + 되감기 화살표. 보기(eye)와 뜻이 갈린다(73차 후반 fe-org 요청) */
  history: ["M3 12a9 9 0 1 0 3-6.7L3 8", "M3 3v5h5", "M12 7v5l3 2"],
  info: ["M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20z", "M12 16v-4", "M12 8h.01"],
  lock: [
    "M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z",
    "M8 11V7a4 4 0 0 1 8 0v4",
  ],
  menu: ["M4 6h16", "M4 12h16", "M4 18h16"],
  /** 가로 점 셋(⋯) — 행 메뉴 */
  more: [
    "M12 11a1 1 0 1 0 0 2 1 1 0 1 0 0-2z",
    "M5 11a1 1 0 1 0 0 2 1 1 0 1 0 0-2z",
    "M19 11a1 1 0 1 0 0 2 1 1 0 1 0 0-2z",
  ],
  plus: ["M12 5v14", "M5 12h14"],
  puzzle: [
    "M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z",
  ],
  refresh: ["M21 12a9 9 0 1 1-2.64-6.36", "M21 3v6h-6"],
  search: ["M11 4a7 7 0 1 0 0 14 7 7 0 1 0 0-14z", "m20 20-4-4"],
  settings: [
    "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z",
    "M12 9a3 3 0 1 0 0 6 3 3 0 1 0 0-6z",
  ],
  sparkle: [
    "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z",
    "M19 16v4",
    "M17 18h4",
  ],
  trash: [
    "M3 6h18",
    "M8 6V4h8v2",
    "m19 6-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6",
    "M10 11v6",
    "M14 11v6",
  ],
  upload: ["M12 15V3", "m7 8 5-5 5 5", "M5 21h14"],
};

const paths = computed(() => PATHS[props.name]);
</script>

<template>
  <svg
    class="ui-icon"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.75"
    stroke-linecap="round"
    stroke-linejoin="round"
    focusable="false"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : 'true'"
  >
    <path v-for="d in paths" :key="d" :d="d" />
  </svg>
</template>
